import React, { useEffect, useMemo, useState, useCallback } from "react";
import ProductImage from "../Shop/ProductImage";
import { useProfil } from "../../contexts/ProfilContext";
import { formatCHF } from "../../utils/formatCHF";

/**
 * ============================================================
 * ProductModalLuxury.jsx
 * ============================================================
 *
 * TEMPLATE MAINTENEUR — GUIDE MANUEL
 * ------------------------------------------------------------
 * RÔLE DE CE COMPOSANT
 * ------------------------------------------------------------
 * Cette modale :
 * - affiche un produit premium en grand format
 * - montre l’histoire produit
 * - expose la lecture du prix public / membre
 * - permet un ajout au panier
 *
 * BUG MÉTIER TRAITÉ DANS CE PATCH
 * ------------------------------------------------------------
 * Le pricing dans cette modale était contractuellement flou :
 *
 * - lecture de product.memberPrice
 * - lecture de audit.memberPrice
 * - ailleurs le moteur pricing exposait surtout targetPrice
 *
 * Cela créait des divergences entre :
 * - catalogue
 * - modale luxe
 * - panier
 * - tunnel
 *
 * OBJECTIF DE CE PATCH
 * ------------------------------------------------------------
 * 1) unifier la lecture des sources pricing
 * 2) exposer clairement :
 *    - prix public
 *    - prix membre
 *    - prix affiché courant
 * 3) envoyer au panier un item enrichi, re-pricable plus tard
 *
 * IMPORTANT
 * ------------------------------------------------------------
 * Ce composant doit rester compatible avec App.js, qui appelle :
 *
 *   <ProductModalLuxury
 *     product={luxuryProduct}
 *     onAddToCart={() => {
 *       ajouterAuPanier(luxuryProduct);
 *       closeLuxury();
 *     }}
 *   />
 *
 * Pour rester drop-in sans casser App.js,
 * on garde la prop onAddToCart,
 * MAIS on lui envoie désormais un item enrichi en argument.
 *
 * Si le parent ignore l’argument, aucun crash.
 * Si le parent l’utilise, il profite du payload enrichi.
 *
 * ============================================================
 */

/**
 * ============================================================
 * GUIDE — NORMALISATION CENTIMES
 * ============================================================
 */
const toCents = (value) => {
  if (value === null || value === undefined || value === "") return null;

  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;

  if (Number.isInteger(n) && n >= 100) {
    return Math.round(n);
  }

  return Math.round(n * 100);
};

/**
 * ============================================================
 * GUIDE — BUILD ITEM PANIER ENRICHI
 * ============================================================
 *
 * RÔLE
 * ------------------------------------------------------------
 * Construire un item panier robuste à partir du produit ouvert
 * dans la modale luxe.
 *
 * IMPORTANT
 * ------------------------------------------------------------
 * On transporte explicitement :
 * - price / displayPrice / appliedPrice
 * - basePrice / publicPrice
 * - memberPrice
 * - old_price
 * - pricePro / prix_connecte / prix_membre
 * - pricingMode
 *
 * Cela permet au panier de se re-pricer plus tard après login/logout.
 */
const buildRichCartItemFromModal = (product, pricingSnapshot) => {
  const displayPrice =
    toCents(pricingSnapshot?.displayPrice) ??
    toCents(product?.displayPrice) ??
    toCents(product?.price) ??
    0;

  const basePrice =
    toCents(pricingSnapshot?.basePrice) ??
    toCents(pricingSnapshot?.displayBasePrice) ??
    toCents(product?.basePrice) ??
    toCents(product?.publicPrice) ??
    toCents(product?.old_price) ??
    toCents(product?.oldPrice) ??
    toCents(product?.price) ??
    displayPrice;

  const memberPrice =
    toCents(pricingSnapshot?.memberPrice) ??
    toCents(pricingSnapshot?.targetPrice) ??
    toCents(product?.memberPrice) ??
    toCents(product?.pricePro) ??
    toCents(product?.prix_connecte) ??
    toCents(product?.prix_membre) ??
    null;

  const oldPrice =
    toCents(pricingSnapshot?.displayBasePrice) ??
    toCents(product?.old_price) ??
    toCents(product?.oldPrice) ??
    (memberPrice !== null && memberPrice < basePrice ? basePrice : null);

  return {
    ...product,

    // ----------------------------------------------------------
    // QUANTITÉ PAR DÉFAUT
    // ----------------------------------------------------------
    qty: 1,
    grams: Number(product?.grams || 0) || 0,
    unitLabel: product?.unitLabel || product?.unite || "pièce",

    // ----------------------------------------------------------
    // PRIX APPLIQUÉ MAINTENANT
    // ----------------------------------------------------------
    price: displayPrice,
    displayPrice,
    appliedPrice: displayPrice,

    // ----------------------------------------------------------
    // SOURCES PRIX
    // ----------------------------------------------------------
    basePrice,
    publicPrice: basePrice,
    memberPrice,
    old_price: oldPrice,

    // ----------------------------------------------------------
    // COMPATIBILITÉ LEGACY MEMBRE
    // ----------------------------------------------------------
    pricePro:
      toCents(product?.pricePro) ??
      toCents(product?.memberPrice) ??
      toCents(pricingSnapshot?.targetPrice),

    prix_connecte:
      toCents(product?.prix_connecte) ??
      toCents(product?.memberPrice) ??
      toCents(product?.pricePro) ??
      toCents(pricingSnapshot?.targetPrice),

    prix_membre:
      toCents(product?.prix_membre) ??
      toCents(product?.memberPrice) ??
      toCents(product?.pricePro) ??
      toCents(pricingSnapshot?.targetPrice),

    // ----------------------------------------------------------
    // MÉTA PRICING
    // ----------------------------------------------------------
    pricingMode:
      pricingSnapshot?.pricingMode || product?.pricingMode || "PUBLIC",
    audit: product?.audit || product?._audit || null,
  };
};

export default function ProductModalLuxury({
  product,
  onClose,
  onUnlock,
  onAddToCart,
  t,
}) {
  const { authStatus } = useProfil();
  const isVisitor = authStatus !== "connected";

  const [storyOpen, setStoryOpen] = useState(false);
  const [justAdded, setJustAdded] = useState(false);

  /**
   * ============================================================
   * GUIDE — DISPONIBILITÉ
   * ============================================================
   */
  const isOnline = useMemo(() => {
    return (
      Number(product?.isOnline ?? product?.is_online ?? 0) === 1 ||
      product?.isOnline === true
    );
  }, [product]);

  const stock = useMemo(() => Number(product?.stock ?? 0) || 0, [product]);
  const canAddToCart = isOnline && stock > 0;
  const isLowStock = canAddToCart && stock <= 3;

  /**
   * ============================================================
   * GUIDE — TEXTE STORY
   * ============================================================
   */
  const storyText = useMemo(() => {
    return (
      product?.histoire ??
      product?.storytelling ??
      product?.story ??
      product?.history ??
      product?.storyText ??
      product?.brandStory ??
      product?.narrative ??
      product?.description_longue ??
      ""
    );
  }, [product]);

  /**
   * ============================================================
   * GUIDE — SNAPSHOT PRICING CANONIQUE
   * ============================================================
   *
   * IMPORTANT
   * ------------------------------------------------------------
   * On évite ici le contrat flou précédent.
   *
   * Ordre logique :
   * - displayPrice       = prix affiché courant
   * - basePrice          = prix public / visiteur
   * - memberPrice        = prix membre / connecté
   * - displayBasePrice   = prix barré si affichable
   * - pricingMode        = mode courant
   *
   * Sources lues :
   * - audit (si présent)
   * - produit enrichi
   * - fallback legacy
   */
  const pricingSnapshot = useMemo(() => {
    const audit = product?.audit || product?._audit || {};

    const displayPrice =
      toCents(audit?.displayPrice) ??
      toCents(product?.displayPrice) ??
      toCents(product?.appliedPrice) ??
      toCents(product?.price) ??
      0;

    const basePrice =
      toCents(audit?.basePrice) ??
      toCents(audit?.displayBasePrice) ??
      toCents(product?.basePrice) ??
      toCents(product?.publicPrice) ??
      toCents(product?.old_price) ??
      toCents(product?.oldPrice) ??
      toCents(product?.prix_barre) ??
      toCents(product?.price) ??
      displayPrice;

    const memberPrice =
      toCents(audit?.memberPrice) ??
      toCents(audit?.targetPrice) ??
      toCents(product?.memberPrice) ??
      toCents(product?.priceMember) ??
      toCents(product?.pricePro) ??
      toCents(product?.prix_connecte) ??
      toCents(product?.prix_membre) ??
      null;

    const displayBasePrice =
      toCents(audit?.displayBasePrice) ??
      toCents(product?.old_price) ??
      toCents(product?.oldPrice) ??
      (memberPrice !== null && memberPrice < basePrice ? basePrice : null);

    return {
      displayPrice,
      basePrice,
      memberPrice,
      displayBasePrice,
      pricingMode: audit?.pricingMode || product?.pricingMode || "PUBLIC",
    };
  }, [product]);

  /**
   * ============================================================
   * GUIDE — FORMATAGE AFFICHAGE MODALE
   * ============================================================
   */
  const pricing = useMemo(() => {
    const baseFmt =
      pricingSnapshot?.basePrice != null
        ? formatCHF(pricingSnapshot.basePrice)
        : null;

    const memberFmt =
      pricingSnapshot?.memberPrice != null
        ? formatCHF(pricingSnapshot.memberPrice)
        : null;

    const currentFmt =
      pricingSnapshot?.displayPrice != null
        ? formatCHF(pricingSnapshot.displayPrice)
        : null;

    const hasRealMemberAdvantage =
      pricingSnapshot?.memberPrice != null &&
      pricingSnapshot?.basePrice != null &&
      pricingSnapshot.memberPrice < pricingSnapshot.basePrice;

    return {
      baseFmt,
      memberFmt,
      currentFmt,
      hasRealMemberAdvantage,
    };
  }, [pricingSnapshot]);

  const handleUnlock = useCallback(() => {
    onUnlock?.();
    onClose?.();
  }, [onUnlock, onClose]);

  /**
   * ============================================================
   * GUIDE — AJOUT AU PANIER DEPUIS LA MODALE
   * ============================================================
   *
   * IMPORTANT
   * ------------------------------------------------------------
   * On n’envoie plus seulement le produit brut.
   * On envoie un item enrichi pricing-complet.
   *
   * Compat App.js :
   * - si le parent attend zéro argument, pas de casse
   * - si le parent accepte un argument, il récupère mieux
   */
  const handleAdd = useCallback(() => {
    if (!canAddToCart || justAdded) return;

    const enriched = buildRichCartItemFromModal(product, pricingSnapshot);

    onAddToCart?.(enriched);
    setJustAdded(true);

    window.setTimeout(() => {
      onClose?.();
    }, 850);
  }, [canAddToCart, justAdded, product, pricingSnapshot, onAddToCart, onClose]);

  /**
   * ============================================================
   * GUIDE — ESCAPE
   * ============================================================
   */
  useEffect(() => {
    if (!product) return;

    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (storyOpen) setStoryOpen(false);
      else onClose?.();
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [product, storyOpen, onClose]);

  /**
   * ============================================================
   * GUIDE — RESET ÉTAT À CHAQUE PRODUIT
   * ============================================================
   */
  useEffect(() => {
    setStoryOpen(false);
    setJustAdded(false);
  }, [product?.id]);

  /**
   * ============================================================
   * GUIDE — SCROLL LOCK BODY
   * ============================================================
   */
  useEffect(() => {
    if (!product) return;

    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    const scrollbarWidth =
      window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [product]);

  if (!product) return null;

  return (
    <div
      className="pm-overlay"
      onClick={() => {
        if (storyOpen) setStoryOpen(false);
        else onClose?.();
      }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className={`pm-modal ${storyOpen ? "story-open" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="pm-close"
          onClick={() => {
            if (storyOpen) setStoryOpen(false);
            else onClose?.();
          }}
          aria-label="Fermer"
          type="button"
        >
          ×
        </button>

        <div className={`pm-left ${storyOpen ? "story-open" : ""}`}>
          <div className="pm-image-topbar">
            <span className="pm-chip">
              {t?.reservedOffer || "OFFRE RÉSERVÉE"}
            </span>

            {isLowStock && (
              <span className="pm-chip danger">
                {t?.lowStock || `Plus que ${stock} dispo`}
              </span>
            )}
          </div>

          <div className={`pm-left-flip ${storyOpen ? "is-flipped" : ""}`}>
            <div className="pm-left-face pm-left-front" aria-hidden={storyOpen}>
              <div className="pm-image-wrap">
                <ProductImage product={product} className="pm-img-full" />

                {!!storyText && (
                  <button
                    type="button"
                    className="pm-story-cta"
                    onClick={() => setStoryOpen(true)}
                    aria-label="Découvrir l’histoire du produit"
                  >
                    📖{" "}
                    {t?.historyOpen ||
                      t?.discoverStory ||
                      "DÉCOUVRIR L’HISTOIRE"}
                  </button>
                )}
              </div>
            </div>

            <div className="pm-left-face pm-left-back" aria-hidden={!storyOpen}>
              <div className="pm-story-back">
                <div className="pm-story-back-head">
                  <div>
                    <div className="pm-story-kicker">
                      {t?.historyTitleKicker || "L’histoire"}
                    </div>

                    <div className="pm-story-title">
                      {product?.name || product?.titre || "Produit"}
                    </div>
                  </div>

                  <button
                    type="button"
                    className="pm-story-back-btn"
                    onClick={() => setStoryOpen(false)}
                    aria-label="Retour au produit"
                  >
                    ← {t?.historyClose || "Retour"}
                  </button>
                </div>

                <div className="pm-story-scroll" role="document">
                  <div className="pm-story-text">{storyText || "—"}</div>
                </div>

                <div className="pm-story-foot">
                  <button
                    type="button"
                    className="pm-secondary"
                    onClick={() => setStoryOpen(false)}
                  >
                    ← {t?.historyClose || "Retour au produit"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="pm-right">
          <div className="pm-sticky-head">
            <h2 className="pm-title">
              {product?.name || product?.titre || "Produit"}
            </h2>

            <div className="pm-sub">
              <span className="pm-sub-line">
                {t?.perUnit || "L'unité :"} {product?.unite || "pièce"}
              </span>
              {!isOnline && <span className="pm-sub-line muted">Vitrine</span>}
            </div>

            <div className="pm-accent" />

            <div className="pm-price-grid">
  <div className="pm-price-col">
    <div className="pm-price-label">
      {t?.basePrice || "PRIX DE BASE"}
    </div>
    <div className="pm-price-value base">
      {pricing.baseFmt || "—"}
    </div>
  </div>

  <div className="pm-price-col member">
    <div className="pm-price-label member">
      🔒 {t?.memberPriceOffer?.replace("(OFFRE VUE)", "") || "PRIX MEMBRE"}
    </div>
    <div className="pm-price-value member">
      {pricing.memberFmt || "—"}
    </div>
  </div>
</div>

            <div className="pm-actions-top">
              {canAddToCart ? (
                <button
                  className={`pm-add-btn ${justAdded ? "is-added" : ""}`}
                  onClick={handleAdd}
                  type="button"
                  disabled={justAdded}
                >
                  {justAdded
                    ? t?.addedToCart || "✓ AJOUTÉ"
                    : isVisitor
                    ? t?.addToCartPublic || "Ajouter au panier (Prix Public)"
                    : t?.addToCart || "Ajouter au panier"}
                </button>
              ) : (
                <button
                  className="pm-add-btn"
                  disabled
                  type="button"
                  style={{ opacity: 0.6 }}
                >
                  {!isOnline ? "Indisponible à la vente" : "Rupture de stock"}
                </button>
              )}

              <button className="pm-secondary" type="button" onClick={onClose}>
  {t?.continueShopping || "Continuer vos achats"}
</button>

{isVisitor && pricing.hasRealMemberAdvantage && (
  <button className="pm-unlock-btn" onClick={handleUnlock} type="button">
    {t?.unlockCta || "DÉBLOQUER CE TARIF"}
  </button>
)}
</div>
</div>

<div className="pm-right-scroll">
  <div className="pm-content-wrapper">
    <div className="pm-highlights">
      <div className="pm-highlight">
        <span className="dot" />{" "}
        {t?.highlightSelected || "Sélection premium"}
      </div>

      <div className="pm-highlight">
        <span className="dot" />{" "}
        {canAddToCart
          ? isLowStock
            ? t?.highlightLimited || "Stock limité"
            : t?.highlightAvailable || "Disponible"
          : t?.highlightUnavailable || "Indisponible"}
      </div>
    </div>

    {isVisitor && pricing.hasRealMemberAdvantage && (
      <div className="pm-member-block">
        <div className="pm-visitor-upsell">
          <p className="pm-unlock-text">
            {t?.unlockText
              ? t.unlockText.replace("{price}", pricing.memberFmt || "—")
              : `Connectez-vous pour débloquer le tarif de ${
                  pricing.memberFmt || "—"
                }.`}
          </p>
        </div>
      </div>
    )}

              {(product?.description || product?.desc || product?.details) && (
                <div className="pm-desc">
                  {product?.description || product?.desc || product?.details}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <style jsx="true">{`
  .pm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.68);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 20px;
  }

  .pm-modal {
    position: relative;
    width: min(460px, 94vw);
    height: min(500px, 82vh);
    background: rgba(251, 248, 241, 0.96);
    display: grid;
    grid-template-columns: 0.85fr 1.15fr;
    box-shadow: 0 24px 70px rgba(0, 0, 0, 0.32);
    border: 1px solid rgba(176, 138, 69, 0.16);
    border-radius: 22px;
    overflow: hidden;
    animation: dealCard 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275)
      forwards;
  }

  @keyframes dealCard {
    0% {
      transform: translateY(100vh) rotateZ(-15deg) rotateY(-180deg);
      opacity: 0;
    }
    100% {
      transform: translateY(0) rotateZ(0deg) rotateY(0deg);
      opacity: 1;
    }
  }

  .pm-close {
    position: absolute;
    top: 12px;
    right: 15px;
    border: none;
    background: rgba(255, 255, 255, 0.92);
    width: 28px;
    height: 28px;
    border-radius: 50%;
    font-size: 20px;
    cursor: pointer;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .pm-left {
    background: rgba(251, 248, 241, 0.92);
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
    padding: 8px;
    gap: 8px;
    min-height: 0;
  }

  .pm-image-topbar {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    padding: 2px 2px 0 2px;
    flex: 0 0 auto;
  }

  .pm-chip {
    font-size: 9px;
    letter-spacing: 0.9px;
    font-weight: 800;
    color: #111;
    text-transform: uppercase;
    background: rgba(212, 175, 55, 0.14);
    border: 1px solid rgba(212, 175, 55, 0.35);
    padding: 5px 8px;
    border-radius: 999px;
  }

  .pm-chip.danger {
    background: rgba(231, 76, 60, 0.1);
    border: 1px solid rgba(231, 76, 60, 0.3);
  }

  .pm-left-flip {
    position: relative;
    flex: 1 1 auto;
    min-height: 0;
    transform-style: preserve-3d;
    transition: transform 0.65s cubic-bezier(0.2, 0.8, 0.2, 1);
  }

  .pm-left-flip.is-flipped {
    transform: rotateY(180deg);
  }

  .pm-left-face {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
  }

  .pm-left-front {
    transform: rotateY(0deg);
  }

  .pm-left-back {
    transform: rotateY(180deg);
    background: rgba(251, 248, 241, 0.96);
  }

  .pm-image-wrap {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border-radius: 16px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.55);
  }

  :global(.pm-img-full) {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  :global(.pm-img-full img) {
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    display: block;
  }

  .pm-story-cta {
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: 10px;
    z-index: 10;
    border: 1px solid rgba(255, 255, 255, 0.22);
    background: rgba(0, 0, 0, 0.55);
    color: #fff;
    padding: 9px 10px;
    border-radius: 10px;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: 1.3px;
    text-transform: uppercase;
    cursor: pointer;
    backdrop-filter: blur(6px);
  }

  .pm-story-cta:hover {
    background: rgba(0, 0, 0, 0.68);
  }

  .pm-story-back {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 12px;
    gap: 12px;
    justify-content: flex-start;
  }

  .pm-story-back-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    flex: 0 0 auto;
  }

  .pm-story-kicker {
    font-size: 10px;
    letter-spacing: 2px;
    text-transform: uppercase;
    opacity: 0.7;
  }

  .pm-story-title {
    font-family: "Playfair Display", serif;
    font-size: 16px;
    font-weight: 700;
    margin-top: 4px;
  }

  .pm-story-back-btn {
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: rgba(255, 255, 255, 0.9);
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;
  }

  .pm-story-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding-right: 6px;
  }

  .pm-story-text {
    font-size: 14px;
    line-height: 1.7;
    color: #2b2b2b;
    white-space: pre-wrap;
    background: rgba(212, 175, 55, 0.05);
    border-left: 2px solid rgba(212, 175, 55, 0.7);
    padding: 12px 14px;
    border-radius: 10px;
  }

  .pm-story-foot {
    flex: 0 0 auto;
  }

  .pm-right {
    background: rgba(251, 248, 241, 0.96);
    border-left: 1px solid rgba(176, 138, 69, 0.12);
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
  }

  .pm-sticky-head {
    padding: 16px 18px 12px 18px;
    border-bottom: 1px solid rgba(176, 138, 69, 0.12);
    background: rgba(251, 248, 241, 0.96);
    z-index: 2;
  }

  .pm-right-scroll {
    padding: 12px 18px 16px 18px;
    overflow: auto;
    min-height: 0;
    -webkit-overflow-scrolling: touch;
  }

  .pm-content-wrapper {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 0;
  }

  .pm-title {
    font-family: "Playfair Display", serif;
    font-size: 20px;
    line-height: 1.18;
    color: #111;
    margin: 0;
  }

  .pm-sub {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    font-size: 12px;
    color: #555;
    margin-top: 6px;
  }

  .pm-sub-line.muted {
    color: #999;
  }

  .pm-accent {
    width: 30px;
    height: 2px;
    background: #d4af37;
    margin-top: 10px;
  }

  .pm-price-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
  align-items: end;
}

.pm-price-col.member {
  text-align: right;
}

.pm-price-label.member {
  color: #b08a45;
}

.pm-price-value.member {
  color: #d4af37;
}

  .pm-price-label {
    font-size: 9px;
    color: #999;
    font-weight: 700;
    text-transform: uppercase;
  }

  .pm-price-value {
    font-size: 16px;
    font-weight: 900;
  }

  .pm-price-value.base {
    color: #111;
  }

  .pm-actions-top {
    margin-top: 14px;
  }

  .pm-highlights {
    display: grid;
    gap: 6px;
    padding: 10px 12px;
    border: 1px solid rgba(176, 138, 69, 0.12);
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.48);
  }

  .pm-highlight {
    display: flex;
    gap: 8px;
    align-items: center;
    font-size: 12px;
    color: #444;
  }

  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #d4af37;
    display: inline-block;
    flex: 0 0 6px;
  }

  .pm-member-block {
    background: rgba(253, 250, 240, 0.82);
    border: 1px solid rgba(212, 175, 55, 0.75);
    border-radius: 12px;
    padding: 13px;
  }

  .pm-member-price-big {
    font-size: 28px;
    font-weight: 900;
    color: #d4af37;
    margin: 4px 0;
  }

  .pm-unlock-btn {
    width: 100%;
    background: #d4af37;
    color: #fff;
    border: none;
    padding: 10px;
    font-weight: 800;
    font-size: 11px;
    text-transform: uppercase;
    cursor: pointer;
    border-radius: 8px;
  }

  .pm-unlock-text {
    margin: 8px 0 10px 0;
    font-size: 12px;
    color: #555;
    line-height: 1.4;
  }

  .pm-desc {
    font-size: 12px;
    color: #555;
    line-height: 1.5;
  }

  .pm-add-btn {
    width: 100%;
    background: #111;
    color: #fff;
    border: none;
    padding: 12px;
    font-weight: 800;
    font-size: 11px;
    text-transform: uppercase;
    cursor: pointer;
    border-radius: 10px;
    transition: transform 0.18s ease, background 0.18s ease,
      box-shadow 0.18s ease;
  }

  .pm-add-btn.is-added {
    background: #d4af37;
    box-shadow: 0 10px 22px rgba(212, 175, 55, 0.24);
    transform: translateY(-1px);
  }

  .pm-secondary {
    width: 100%;
    margin-top: 8px;
    background: transparent;
    color: #111;
    border: 1px solid rgba(176, 138, 69, 0.18);
    padding: 10px;
    font-weight: 700;
    font-size: 11px;
    text-transform: uppercase;
    cursor: pointer;
    border-radius: 10px;
  }

  @media (max-width: 720px) {
    .pm-overlay {
      padding: 14px;
    }

    .pm-modal {
      grid-template-columns: 1fr;
      width: min(380px, 92vw);
      height: auto;
      max-height: 59vh;
      overflow-y: auto;
      border-radius: 22px;
    }

    .pm-left {
      height: 240px;
      padding: 8px;
      gap: 8px;
    }

    .pm-modal.story-open {
      height: auto;
      max-height: 74vh;
      grid-template-rows: minmax(250px, 58vh) auto;
    }

    .pm-left.story-open {
      height: auto;
      min-height: min(250px, 38vh);
    }

    .pm-story-back {
      padding: 12px;
      gap: 12px;
    }

    .pm-story-scroll {
      padding-right: 2px;
    }

    .pm-story-text {
      font-size: 13px;
      line-height: 1.65;
      padding: 12px 12px;
    }

    .pm-sticky-head {
      padding: 12px 16px 10px 16px;
    }

    .pm-right-scroll {
      padding: 10px 16px calc(16px + env(safe-area-inset-bottom)) 16px;
    }

    .pm-content-wrapper {
      gap: 10px;
    }

    .pm-title {
      font-size: 16px;
      line-height: 1.15;
    }

    .pm-sub {
      margin-top: 4px;
      font-size: 11px;
    }

    .pm-accent {
      margin-top: 8px;
    }

    .pm-price-grid {
      margin-top: 10px;
      gap: 10px;
    }

    .pm-actions-top {
      margin-top: 10px;
    }

    .pm-add-btn {
      padding: 12px;
      font-size: 11px;
    }

    .pm-secondary {
      margin-top: 8px;
      padding: 11px;
      font-size: 11px;
    }

    .pm-highlights {
      gap: 4px;
      padding: 8px 10px;
    }

    .pm-highlight {
      font-size: 11px;
    }

    .pm-member-block {
      padding: 12px;
    }

    .pm-member-price-big {
      font-size: 26px;
      line-height: 1.05;
    }

    .pm-unlock-text {
      margin: 6px 0 8px 0;
      font-size: 11px;
      line-height: 1.35;
    }

    .pm-unlock-btn {
      padding: 10px;
      font-size: 10.5px;
    }

    .pm-desc {
      font-size: 11px;
      line-height: 1.45;
    }
  }
`}</style>
    </div>
  );
}
